import {
  IconWarning20,
  RadioButton,
  RadioGroup,
  Tooltip,
} from '@lightspeed/unified-components-helios-theme/react'
import { T, useT } from '@transifex/react'
import React from 'react'
import { OutletSelect } from '../../OutletSelect'
import {
  DisplayOption,
  LineItemFulfilmentDecisionViewProps,
  useLineItemFulfilmentDecisionViewModel,
} from './useLineItemFulfilmentDecisionViewModel'

export const TContext = 'Line item fulfilment popover'
export const TestId = 'lif-decision-view'

export const LineItemFulfilmentDecisionView: React.FC<LineItemFulfilmentDecisionViewProps> = props => {
  const {
    productId,
    productQuantity,
    productName,
    initialSelectedOutletId,
    canShowBreakdownOption = false,
    outerProduct,
    saleHasServiceOrder = false,
  } = props
  const t = useT()
  const model = useLineItemFulfilmentDecisionViewModel(props)

  return (
    <div className="uc-base">
      <div className="flex flex-col gap-3 font-general">
        <div className="flex items-center gap-1">
          <IconWarning20 className="text-no-soft min-w-5" />
          <h3 className="text-heading-4">
            {t(
              '{productQuantity, plural,' +
                'one {{productQuantity} item available}' +
                'other {{productQuantity} items available}}',
              { _context: TContext, productQuantity, productName }
            )}
          </h3>
        </div>
        <p className="text-neutral-default">
          <T
            _str="Sell now and continue with negative inventory or fulfill the sale later."
            _context="Popover description"
          />
        </p>
        <RadioGroup
          selectedKey={model.selectedDisplayOption}
          onSelect={(key: string) =>
            model.handleOptionSelect(key as DisplayOption)
          }
        >
          {saleHasServiceOrder ? (
            <RadioButton
              value={DisplayOption.CONTINUE_ANYWAY}
              labelSlot={t("Add product anyway. It's available.", {
                _context: TContext,
              })}
              data-track="continue-anyway-option"
              customClasses={{
                buttonContainer: 'gap-2',
              }}
            />
          ) : (
            <RadioButton
              value={DisplayOption.CONTINUE_ANYWAY}
              labelSlot={t('Sell now anyway', {
                _context: 'Radio button text',
              })}
              descriptionSlot={t('Inventory at this outlet will go negative', {
                _context: 'Radio button description',
              })}
              data-track="continue-anyway-option"
              customClasses={{
                buttonContainer: 'gap-2',
                buttonDescription: 'text-neutral-soft',
              }}
            />
          )}
          {canShowBreakdownOption && (
            <RadioButton
              value={DisplayOption.BREAKDOWN}
              labelSlot={t('Break down from "{outerProductName}"', {
                _context: TContext,
                outerProductName:
                  outerProduct?.name ??
                  t('outer product', {
                    _context: 'outer product that is broken down',
                  }),
              })}
              data-track="breakdown-option"
              customClasses={{
                buttonContainer: 'gap-2',
              }}
            />
          )}
          <Tooltip
            placement="bottom"
            disabled={!model.isLaybySale}
            controlSlot={
              saleHasServiceOrder ? (
                <RadioButton
                  value={DisplayOption.FULFIL_LATER}
                  labelSlot={t('Request to order this product', {
                    _context: TContext,
                  })}
                  data-track="fulfil-later-option"
                  disabled={model.isLaybySale}
                  customClasses={{
                    buttonContainer: 'gap-2',
                  }}
                />
              ) : (
                <RadioButton
                  value={DisplayOption.FULFIL_LATER}
                  labelSlot={t('Set up fulfillment', {
                    _context: 'Radio button text',
                  })}
                  descriptionSlot={
                    model.hasCLFOutletPermission
                      ? t(
                          'Fulfill the sale by creating a special order or using stock from other outlets',
                          {
                            _context: 'Radio button description',
                          }
                        )
                      : t('Fulfill the sale by creating a special order', {
                          _context: 'Radio button description',
                        })
                  }
                  data-track="fulfil-later-option"
                  disabled={model.isLaybySale}
                  customClasses={{
                    buttonContainer: 'gap-2',
                    buttonDescription: 'text-neutral-soft',
                  }}
                />
              )
            }
          >
            {t('Delivery and pickup aren’t available for layaway sales.', {
              _context: 'Tooltip text',
            })}
          </Tooltip>
        </RadioGroup>
        {model.shouldShowOutletSelect && (
          <OutletSelect
            className="vd-pl6 w-[324px]"
            initialSelectedOutletId={initialSelectedOutletId}
            includeAvailability
            onOutletChange={outlet => model.handleOutletSelect(outlet.id)}
            onSelectedOutletIdChange={model.handleOutletSelect}
            productId={productId}
          />
        )}
      </div>
    </div>
  )
}
